import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { news } from "@/mocks/news";

const PAGE_SIZE = 6;
const categoryOptions = ["全部", "活动", "访谈", "花絮", "作品动态"];
const rangeOptions = [
  { key: "all", label: "全部时间", days: 9999 },
  { key: "m1", label: "近 1 个月", days: 31 },
  { key: "m3", label: "近 3 个月", days: 93 },
  { key: "m6", label: "近半年", days: 186 },
];

export default function News() {
  useDocumentTitle(
    "杨幂最新动态 - 明星资讯平台",
    "杨幂最新动态与明星新闻，涵盖活动、访谈、花絮与作品动态，按时间与类别筛选，第一时间掌握杨幂的每条资讯。",
    "杨幂最新动态,杨幂新闻,杨幂活动,杨幂访谈",
  );

  const [category, setCategory] = useState("全部");
  const [range, setRange] = useState("all");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const now = new Date("2026-10-14").getTime();
    const rangeCfg = rangeOptions.find((r) => r.key === range) ?? rangeOptions[0];
    return [...news]
      .filter((n) => (category === "全部" ? true : n.category === category))
      .filter((n) => (now - new Date(n.date).getTime()) / 86400000 <= rangeCfg.days)
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [category, range]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageList = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const reset = () => {
    setCategory("全部");
    setRange("all");
    setPage(1);
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "资讯动态" }]} />

      <div className="mt-5 mb-8">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">杨幂最新动态与明星新闻</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl">
          汇总杨幂的活动、访谈、花絮与作品动态，支持按时间区间与新闻类别筛选，按发布时间倒序呈现。
        </p>
      </div>

      <div className="bg-background-100/70 border border-background-200 rounded-lg p-4 md:p-5 mb-8 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
        <div className="flex items-center gap-3">
          <span className="text-sm font-label text-foreground-600 whitespace-nowrap">类别</span>
          <div className="flex items-center gap-1 bg-background-50 rounded-full p-1 border border-background-200 overflow-x-auto max-w-full">
            {categoryOptions.map((c) => (
              <button
                key={c}
                onClick={() => { setCategory(c); setPage(1); }}
                className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                  c === category ? "bg-primary-500 text-background-50 font-semibold" : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 lg:ml-auto">
          <span className="text-sm font-label text-foreground-600 whitespace-nowrap">时间</span>
          <select
            value={range}
            onChange={(e) => { setRange(e.target.value); setPage(1); }}
            className="h-9 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer"
          >
            {rangeOptions.map((r) => (
              <option key={r.key} value={r.key}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      {pageList.length > 0 ? (
        <>
          <div className="flex flex-col gap-4 md:gap-5">
            {pageList.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination current={currentPage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-4">
            <i className="ri-newspaper-line text-3xl" />
          </span>
          <p className="font-heading text-lg font-semibold text-foreground-800">暂无相关新闻</p>
          <p className="mt-2 text-sm text-foreground-500">当前筛选条件下没有内容，试试重置筛选。</p>
          <button
            onClick={reset}
            className="mt-5 inline-flex items-center gap-2 px-5 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      )}
    </div>
  );
}